<script>
export default {
  name: 'OrderListItem'
}
</script>

<script setup>
import { ref, onMounted, onBeforeUpdate, computed, watch, nextTick } from 'vue';
import { tools } from '@functions/tools.js';
import axios from 'axios';
import { url } from '@/config/urls';
import router from '@/router/index.js';

// 定数定義_枠線対応オブジェクト
const borderColorMap = {
  '#008941': '#00cc00',
  '#ff4e00': '#ff8b00',
  '#e80000': '#ff3e5a'
}


const props = defineProps({
  order: {
    type: Object,
    required: true
  },
  deliveryStatuses: {
    type: Array,
    required: true
  },
  goodsTypes: {
    type: Array,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  isSimpleLayout: {
    type: Boolean,
    default: false
  },
  updateCommentApiUrl: {
    type: String,
    default: url.api.updateComment
  },
  loginUrl: {
    type: String,
    default: url.login
  },
  errorUrl: {
    type: String,
    default: url.error
  }
});

const emits = defineEmits(['showFlashMessage', 'updateModalData']);

const isShowEditComment = ref(false);
const disabledEditComment = ref(true);

const comment = ref('');

// 配送ステータス
const currentDeliveryStatus = ref({
  searchKey: '',
  searchName: '',
  code: '',
  name: '',
  unissuedFlg: false,
  amountLabel: '',
  dateLabel: '',
  colorCode: '',
  backgroundPath: '',
  iconPath: ''
});

// エラーメッセージ
const errors = ref({
  common: '',
  orderId: '',
  comment: ''
});

const showError = ref(false);

// 外部定義メソッド
const { mapResponseError, slideUp, slideDown } = tools();

// インデックス
const itemIndex = computed({
  get() {
    return `item_${props.index}`;
  }
});

// 伝票見出し
const chitHeading = computed({
  get() {
    return `${props.order.chitNumber}-${props.order.chitDate}-${props.order.companyName}`;
  }
});

// 発行日時
const occurrenceDateTime = computed({
  get() {
    return `${props.order.occurrenceDay}　${props.order.occurrenceTime}`;
  }
});

// 商品情報
const goodsInfoString = computed({
  get() {
    if (props.order.formalGoodsName2) {
      return `${props.order.janCode}\n${props.order.makerName}\n${props.order.formalGoodsName1}\n${props.order.formalGoodsName2}`
    }else {
      return `${props.order.janCode}\n${props.order.makerName}\n${props.order.formalGoodsName1}`
    }
  }
});

// 納品数
const deliveryAmount = computed({
  get() {
    return (props.order.deliveryOfficeAmount + props.order.deliveryCenterAmount).toLocaleString();
  }
});

// 納品予定日
const deliveryGoodsDay = computed({
  get() {
    const regexpFull = /^[0-9]{4}\/[0-1][0-9]\/[0-3][0-9]$/;
    const regexpOnlyView = /^[0-1][0-9]\/[0-3][0-9]$/;
    if (!props.order.deliveryGoodsDay) {
      return ''
    }
    const trimmedStr = props.order.deliveryGoodsDay.trim();
    if (regexpFull.exec(trimmedStr)) {
      return trimmedStr.slice(5);
    }else {
      return trimmedStr;
    }
  }
})

// 再発注用商品分類名
const reorderGoodsTypeName = computed(() => {
  if (!props.order.reorderGoodsType) {
    return '';
  }

  const goodsType = props.goodsTypes.find(
    goodsType => goodsType.code === props.order.reorderGoodsType
  );

  return goodsType?.name ?? '';
});

// 再発注数
const reorderAmount = computed({
  get() {
    if (props.order.reorderAmount) {
      return props.order.reorderAmount.toLocaleString();
    }
  }
});

// 配送ステータスアイコンキャプション
const deliveryStatusCaption = computed({
  get() {
    switch (currentDeliveryStatus.value.code) {
      case '01':
      case '11': return 'センター\n出荷'; break;
      case '02':
      case '12': return '営業所\n出荷'; break;
      case '05':
      case '15': return '確保済み'; break;
      default: return currentDeliveryStatus.value.name
    }
  }
})

// 配送ステータスアイコンStyle
const deliveryStatusIconStyle = computed({
  get() {
    if (!currentDeliveryStatus.value.iconPath) {
      return {};
    }else {
      return {
        'background-image': `url(${currentDeliveryStatus.value.iconPath})`
      };
    }
  }
});

// 配送背景Style
const deliveryBackgroundStyle = computed({
  get() {
    if (!currentDeliveryStatus.value.backgroundPath || !currentDeliveryStatus.value.colorCode) {
      return {};
    }else {
      return {
        'background-image': `url(${currentDeliveryStatus.value.backgroundPath})`,
        'border-color': borderColorMap[currentDeliveryStatus.value.colorCode]
      };
    }
  }
});

// 配送文字Style
const deliveryTextStyle = computed({
  get() {
    if (!currentDeliveryStatus.value.colorCode) {
      return {};
    }else {
      return {
        'color': currentDeliveryStatus.value.colorCode
      };
    }
  }
});

// 同一成分検索ボタンフラグ
const isShowAllogeneicDrugSearchBtn = ref(false);

// コメント更新エリアの開閉
const openEditArea = (duration) => {
  const tItem = document.getElementById(itemIndex.value).querySelector('.edit_block1')
  if (isShowEditComment.value) {
  	slideUp(tItem, duration);
  	isShowEditComment.value = false;
  	tItem.querySelector('textarea').blur();
  } else {
  	slideDown(tItem, duration);
  	isShowEditComment.value = true;
  }
  disabledEditComment.value = true;
}

// 編集・保存ボタン押下時
const onClickEditBtn = () => {
  const editBtn = document.getElementById(itemIndex.value).querySelector('.edit_block1 .com_btn_tgl1');

  if (!disabledEditComment.value) {
    commentValidation();
    if (!errors.value.comment) {
      updateComment();
  	  disabledEditComment.value = true;
    }
  }else {
  	disabledEditComment.value = false;
  }
}

// 同一成分検索ボタン押下時
const onClickSearchIdenticalFormulation = (event) => {
  emits('updateModalData', props.order);
  $('html').css('scroll-behavior', 'auto');
  event.target.click();
}

const isShowReorderDetail = ref(false);
const reorderDetailBlock = ref(null);
const reorderDetail = ref(null);

// 再発注明細の開閉
const openReorderDetail = (duration) => {
  if (isShowReorderDetail.value) {
    // 詳細を非表示にする場合
    slideUp(reorderDetailBlock.value, duration);
    reorderDetail.value.blur();
  } else {
    // 詳細を表示する場合
    slideDown(reorderDetailBlock.value, duration);
  }
  isShowReorderDetail.value = !isShowReorderDetail.value;
}

// バリデーション
const commentValidation = () => {
  const maxLength = 30;
  errors.value.comment = '';
  if (comment.value.length > maxLength) {
    errors.value.comment = 'コメントは30文字以内で入力してください。'
  }
}

// コメント更新機能
const updateComment = () => {
  const param = {
    orderId: props.order.orderId,
    comment: comment.value
  };
  axios.post(props.updateCommentApiUrl, param)
    .then((response) => {
  	  emits('showFlashMessage', 'comment');
    })
    .catch((error) => {
      switch(error?.response?.status) {
        case 400:
          if (error.response.data.error instanceof Array) {
            errors.value.orderId = mapResponseError(error.response, ['orderId']);
            errors.value.comment = mapResponseError(error.response, ['comment']);
          }
          break;
        case 401:
          router.replace(props.loginUrl);
          break;
        case 500:
          sessionStorage.setItem('errorHeading', 'サーバーエラー。');
          sessionStorage.setItem('errorBody', 'サーバーで問題が発生しました。現在、サービス提供ができない可能性があります。しばらくしてから再度アクセスしてください。');
          location.href = props.errorUrl;
          break;
        default: break;
      }
    });
}

// 配送ステータス表示の更新処理
const updateDeliveryStatus = () => {
  setCurrentDeliveryStatus()
    .then(() => {
        isShowAllogeneicDrugSearchBtn.value = (currentDeliveryStatus.value.code == "91") && !props.order.reorderJanCode;
      }
    );
}

// 注文アイテム内容の初期化処理
const initItem = () => {
  updateDeliveryStatus();

  comment.value = props.order.comment;
  if (comment.value) {
    //必ず開かせる
    isShowEditComment.value = false;
    openEditArea(0);
  }else {
    //必ず閉じさせる
    isShowEditComment.value = true;
    openEditArea(0);
  }
}

// 現在ステータスのセット（Promise）
const setCurrentDeliveryStatus = () => {
  return new Promise((resolve, reject) => {
    currentDeliveryStatus.value = props.deliveryStatuses.find(
        (status) => status.code == props.order.deliveryStatusCode
    ) ?? '00';
    return resolve('処理完了');
  });
}

// 欠品数か納品数か
const isShortageAmount = computed({
  get(){
    if ("欠品数" === currentDeliveryStatus.value.amountLabel) {
      return true;
    }
    return false;
  }
})

onMounted(() => {
  nextTick(() => {
    initItem();
  });
});
watch(() => props.order, () => {
  initItem();
})
watch(() => props.deliveryStatuses, () => {
  updateDeliveryStatus();
})
watch(() => props.isSimpleLayout, () => {
  // レイアウト切替時に再設定
  nextTick(() => {
    initItem();
  });
})
</script>

<template>
  <div>
    <!-- デザイン版レイアウト -->
    <div class="com_data_item1" :id="itemIndex" v-if="!props.isSimpleLayout">
      <div class="row1 n1">
        <div>
          <p>{{ chitHeading }}</p>
          <div class="com_tags1" v-if="props.order.reorderAmount >= 1 ">
            <span class="tag3 reorder_clp_button1" @click="openReorderDetail(200)">再発注済み</span>
          </div>
          <p>{{ occurrenceDateTime }}</p>
        </div>
        <div>
          <button class="com_icon_edit1 toggle1" @click="openEditArea(200)"></button>
        </div>
      </div>
      <hr class="sep1">
      <div class="row1 n2">
        <div>
          <p class="text1">{{ props.order.goodsTypeName }}</p>
          <p class="text2 text-pre-wrap">{{ goodsInfoString }}</p>
          <div class="com_status1">
            <a href="#" class="stl2" data-bs-toggle="modal" data-bs-target="#modal1" :style="deliveryBackgroundStyle" v-if="isShowAllogeneicDrugSearchBtn" @click.prevent="onClickSearchIdenticalFormulation($event)">
              <span class="icon3 sts10a"></span>
            </a>
          </div>
        </div>
        <div>
          <p class="text2 com_right1 com_left1s">{{ props.order.chitMessage }}</p>
          <div class="com_status1" :key="currentDeliveryStatus">
            <div class="stl1" v-bind:style="deliveryBackgroundStyle" v-if="props.order.orderQuantity">
              <span class="txt1">発注数</span>
              <span class="txt2" v-bind:style="deliveryTextStyle">{{ props.order.orderQuantity.toLocaleString() }}</span>
            </div>
            <div class="stl1" v-bind:style="deliveryBackgroundStyle">
              <span class="txt1">{{ currentDeliveryStatus.amountLabel }}</span>
              <span class="txt2" v-bind:style="deliveryTextStyle" v-if="isShortageAmount">{{ props.order.shortageAmount.toLocaleString() }}</span>
              <span class="txt2" v-bind:style="deliveryTextStyle" v-else>{{ deliveryAmount }}</span>
            </div>
            <div class="stl1" v-bind:style="deliveryBackgroundStyle" v-if="deliveryGoodsDay">
              <span class="txt1">{{ currentDeliveryStatus.dateLabel }}</span>
              <span class="txt2" v-bind:style="deliveryTextStyle">{{ deliveryGoodsDay }}</span>
            </div>
            <div class="stl1" v-bind:style="deliveryBackgroundStyle">
              <span class="txt1"></span>
              <span class="icon1" v-bind:style="deliveryStatusIconStyle"></span>
              <span class="txt3 text-pre-wrap"><span>{{ deliveryStatusCaption }}</span></span>
            </div>
            <a href="#" class="stl1 w1 dspb-w" data-bs-toggle="modal" data-bs-target="#modal1" :style="deliveryBackgroundStyle" v-if="isShowAllogeneicDrugSearchBtn" @click.prevent="onClickSearchIdenticalFormulation($event)">
              <span class="icon2 sts9a"></span>
              <span class="txt4" :style="deliveryTextStyle"><span>同一成分<br>検索</span></span>
            </a>
          </div>
        </div>
      </div>
      <div class="reorder_clp_block1" ref="reorderDetailBlock">
        <hr class="sep2">
        <div class="row1" ref="reorderDetail">
          <div>
            <p class="text1">{{ reorderGoodsTypeName }}</p>
            <p class="text2">{{ props.order.reorderJanCode }}<br>
              {{ props.order.reorderMakerName }}<br>
              {{ props.order.reorderFormalGoodsName1 }}<br>
              {{ props.order.reorderFormalGoodsName2 }}
            </p>
          </div>
          <div>
            <p class="text2 com_right1 com_left1s"></p>
            <div class="com_status1 a">
              <div class="stl1 a">
                <span class="txt1">再発注数</span>
                <span class="txt2 a">{{ reorderAmount }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="edit_block1" :class="{ open1: isShowEditComment }">
        <div class="inner1">
          <textarea maxlength="30" class="form-control com_form1" v-bind:disabled="disabledEditComment" :class="{ error: errors.comment }" v-model="comment"></textarea>
          <div class="com_validate_msg1 error" v-if="errors.comment">{{ errors.comment }}</div>
          <button type="button" class="btn com_btn1 com_btn_tgl1" :class="{ on: !disabledEditComment }" @click.prevent="onClickEditBtn"></button>
        </div>
      </div>
    </div>

    <!-- シンプル版レイアウト -->
    <div class="simple_card" :id="itemIndex" v-if="props.isSimpleLayout">
      <div class="simple_card_header">
        <div class="simple_card_header_content">
          <div>
            <p>{{ chitHeading }}</p>
            <div class="com_tags1" v-if="props.order.reorderAmount >= 1">
              <span class="tag3 reorder_clp_button1" @click="openReorderDetail(200)"
                >再発注済み</span
              >
            </div>
          </div>
          <p>{{ occurrenceDateTime }}</p>
        </div>
        <div>
          <button class="com_icon_edit1 toggle1" @click="openEditArea(200)"></button>
        </div>
      </div>
      <div class="simple_card_body">
        <div class="simple_card_body_content">
          <div class="body-upper-half">
            <div class="grid_container">
              <p class="simple_txt_16 simple_blue_bold">{{ props.order.goodsTypeName }}</p>
              <p class="simple_txt_14">{{ props.order.janCode }}</p>
              <p class="simple_txt_18">{{ props.order.makerName }}</p>
              <p class="simple_txt_18">
                {{ props.order.formalGoodsName1 }} {{ props.order.formalGoodsName2 }}
              </p>
            </div>
            <div class="com_status1 pc-no-display" v-if="isShowAllogeneicDrugSearchBtn">
              <a
                href="#"
                class="stl2"
                data-bs-toggle="modal"
                data-bs-target="#modal1"
                v-if="isShowAllogeneicDrugSearchBtn"
                @click.prevent="onClickSearchIdenticalFormulation($event)"
              >
                <span class="icon3 sts10a"></span>
              </a>
            </div>
          </div>
          <div class="body_lower_half">
            <p class="text2">{{ props.order.chitMessage }}</p>

            <div class="com_status1 simple_status_wrap" :key="currentDeliveryStatus">
              <!-- 「発注数」は現在機能がないためコメントアウトとする -->
              <!-- <div
                class="simple_status_wrap_content status-content-layout"
                v-bind:style="deliveryBackgroundStyle"
                v-if="props.order.orderQuantity"
              >
                <span class="simple_status_txt" v-bind:style="deliveryTextStyle">発注数</span>
                <span class="simple_status_txt" v-bind:style="deliveryTextStyle">
                  {{ props.order.orderQuantity.toLocaleString() }}
                </span>
              </div> -->
              <div class="simple_status_wrap_content status_content" v-bind:style="deliveryBackgroundStyle">
                <span class="simple_status_txt simple_txt_14" v-bind:style="deliveryTextStyle">{{ currentDeliveryStatus.amountLabel }}</span>
                <span
                  class="simple_status_num simple_txt_14"
                  v-bind:style="deliveryTextStyle"
                  v-if="isShortageAmount"
                  >{{ props.order.shortageAmount.toLocaleString() }}</span
                >
                <span class="simple_status_num simple_txt_14" v-bind:style="deliveryTextStyle" v-else>{{
                  deliveryAmount
                }}</span>
              </div>
              <div
                class="simple_status_wrap_content status_content"
                v-bind:style="deliveryBackgroundStyle"
                v-if="deliveryGoodsDay"
              >
                <span class="simple_status_txt simple_txt_14" v-bind:style="deliveryTextStyle">{{ currentDeliveryStatus.dateLabel }}</span>
                <span class="simple_status_num simple_txt_14" v-bind:style="deliveryTextStyle">{{
                  deliveryGoodsDay
                }}</span>
              </div>
              <div class="simple_status_wrap_content status_content" v-bind:style="deliveryBackgroundStyle">
                <span class="simple_status_txt simple_txt_14 text-pre-wrap" v-bind:style="deliveryTextStyle">{{ deliveryStatusCaption }}</span>
              </div>
              <a
                href="#"
                class="simple_status_wrap_content status_content simple_drug_search dspb-w"
                data-bs-toggle="modal"
                data-bs-target="#modal1"
                v-if="isShowAllogeneicDrugSearchBtn"
                @click.prevent="onClickSearchIdenticalFormulation($event)"
              >
                <span class="icon3 sts10a drug_search_icon"></span>
              </a>
            </div>

          </div>
        </div>
        <div class="reorder_clp_block1" ref="reorderDetailBlock">
          <hr class="sep2">
          <div class="row1 reorder_detail" ref="reorderDetail">
            <div class="grid_container">
              <p class="simple_blue_bold simple_txt_16">{{ reorderGoodsTypeName }}</p>
              <p class="simple_txt_14">{{ props.order.reorderJanCode }}</p>
              <p class="simple_txt_18">{{ props.order.reorderMakerName }}</p>
              <p class="simple_txt_18">
                {{ props.order.reorderFormalGoodsName1 }} {{ props.order.reorderFormalGoodsName2 }}
              </p>
            </div>
            <div class="container_com_status1">
              <div class="com_status1 sp-no-display simple_status_wrap">
                <div
                  class="simple_status_wrap_content status_content"
                  v-bind:style="deliveryBackgroundStyle"
                >
                  <span class="simple_status_txt simple_txt_14" v-bind:style="deliveryTextStyle">再発注数</span>
                  <span class="simple_status_num simple_txt_14" v-bind:style="deliveryTextStyle">
                    {{ reorderAmount }}
                  </span>
                </div>
              </div>
              <div class="com_status1 pc-no-display simple_status_wrap">
                <div class="simple_status_wrap_content status_content a">
                  <span class="simple_status_txt simple_txt_14" :style="deliveryTextStyle">再発注数</span>
                  <span class="simple_status_num simple_txt_14" :style="deliveryTextStyle">{{ reorderAmount }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="edit_block1" :class="{ open1: isShowEditComment }">
          <div class="inner1">
            <textarea
              maxlength="30"
              class="form-control com_form1"
              v-bind:disabled="disabledEditComment"
              :class="{ error: errors.comment }"
              v-model="comment"
            ></textarea>
            <div class="com_validate_msg1 error" v-if="errors.comment">
              {{ errors.comment }}
            </div>
            <button
              type="button"
              class="btn com_btn1 com_btn_tgl1"
              :class="{ on: !disabledEditComment }"
              @click.prevent="onClickEditBtn"
            ></button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-pre-wrap {
  white-space: pre-wrap
}

@media only screen and (min-width: 822px) {
  /* PC版非表示用クラス */
  .pc-no-display {
    display: none;
  }
}

@media only screen and (max-width: 821px) {
  /* スマホ版非表示用クラス */
  .sp-no-display {
    display: none;
  }
}
</style>
